import { areDetailedCybusServiceEquals, Capability, type CybusDetailedService } from '../../../../../../domain'

import type { BackendDefinition } from '../../../../../Connectware'
import { BaseSubscriptionStrategy } from '../Base'
import { mapDetailedService } from './mappers'

export class DetailedServiceStrategy extends BaseSubscriptionStrategy<never, 'service'> {
    protected override readonly areEquals = areDetailedCybusServiceEquals

    override readonly requiredPermissions = [Capability.SERVICE_READ]

    override retrieveOne (id: CybusDetailedService['id']): Promise<CybusDetailedService> {
        return Promise.all([
            this.request<'/api/v2/services/+', 'GET', BackendDefinition<'service-manager', 'ServiceDetails'>>({
                capability: Capability.SERVICE_READ,
                method: 'GET',
                path: '/api/v2/services/+',
                authenticate: true,
                pathParams: [id],
                handlers: {
                    200: (r) => r.getJson().then(({ data }) => data),
                    404: DetailedServiceStrategy.createNotFoundErrorHandler('/api/services/+', id),
                },
            }),
            this.request<'/api/services/all-links', 'GET', BackendDefinition<'service-manager', 'LinkedService'>['links']>({
                capability: Capability.SERVICE_READ,
                method: 'GET',
                path: '/api/services/all-links',
                queryParams: { onlyFromEnabledServices: 'true' },
                authenticate: true,
                handlers: { 200: (r) => r.getJson().then((data) => data.find((s) => s.id === id)?.links ?? []) },
            }),
            this.request<'/api/services/+/dependencies', 'GET', BackendDefinition<'service-manager', 'ServiceDependencies'>>({
                path: '/api/services/+/dependencies',
                method: 'GET',
                capability: Capability.SERVICE_READ,
                pathParams: [id],
                authenticate: true,
                handlers: {
                    200: (r) => r.getJson(),
                    404: DetailedServiceStrategy.createNotFoundErrorHandler('/api/services/+/dependencies', id),
                },
            }),
            this.rstAdapter.fetchServiceDeviationsCount('service', id),
        ]).then(([params, links, dependencies, resourceDeviationsCount]) => mapDetailedService(params, links, dependencies, resourceDeviationsCount))
    }

    createSingleChangeListener (id: CybusDetailedService['id'], handler: VoidFunction): Promise<VoidFunction> {
        return this.rstAdapter.subscribeToServiceDeviationsCount('service', id, handler)
    }
}
